/**
 * Signup page — split layout matching the login page.
 *
 * Creates a new organization AND the first org_admin user in one
 * step. After successful signup the user is redirected to the
 * dashboard with their new workspace selected.
 */

import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { useAuth } from "../AuthContext.jsx";
import { ManageCareMark } from "../components.jsx";

function slugify(s) {
  return (s || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

export default function Signup() {
  const nav = useNavigate();
  const { signup } = useAuth();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    organization_name: "",
    organization_slug: "",
    organization_description: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const effectiveSlug =
    slugify(form.organization_slug) || slugify(form.organization_name);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (form.password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (!effectiveSlug) {
      setError("Organization slug is required");
      return;
    }
    setBusy(true);
    try {
      await signup({ ...form, organization_slug: effectiveSlug });
      nav("/", { replace: true });
    } catch (err) {
      setError(err.message || "Signup failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-split">
      {/* ─── Left brand panel ─────────────────────────────── */}
      <aside className="auth-brand-panel">
        <div className="auth-brand-top">
          <div className="auth-brand-logo">
            <ManageCareMark size={28} />
          </div>
          <span className="auth-brand-name">Configural</span>
        </div>

        <div className="auth-brand-body">
          <h1 className="auth-brand-headline">
            Ship production voice agents in hours, not weeks.
          </h1>
          <p className="auth-brand-tagline">
            Create your organization to start composing agents, tools, and
            playbooks in a fully isolated workspace.
          </p>

          <ul className="auth-brand-features">
            <li>
              <span className="auth-brand-check">✓</span>
              <div>
                <strong>Start with a clean workspace</strong>
                <p>
                  Your own library, tenants, and prod release history from
                  day one.
                </p>
              </div>
            </li>
            <li>
              <span className="auth-brand-check">✓</span>
              <div>
                <strong>Invite your team</strong>
                <p>
                  Add org-admins who can manage users, agents, and content.
                </p>
              </div>
            </li>
            <li>
              <span className="auth-brand-check">✓</span>
              <div>
                <strong>Import from an existing project</strong>
                <p>
                  Bring agents, protocols, and tools from your current voice
                  platform.
                </p>
              </div>
            </li>
          </ul>
        </div>

        <div className="auth-brand-footer">
          <span>© 2026 Configural</span>
          <span className="auth-brand-dot">·</span>
          <a href="#privacy">Privacy</a>
          <span className="auth-brand-dot">·</span>
          <a href="#terms">Terms</a>
        </div>
      </aside>

      {/* ─── Right form panel ─────────────────────────────── */}
      <main className="auth-form-panel">
        <div className="auth-form-card" style={{ maxWidth: 520 }}>
          <div className="auth-form-mobile-brand">
            <ManageCareMark size={30} />
            <span>Configural</span>
          </div>

          <div className="auth-form-header">
            <h2>Create your organization</h2>
            <p className="muted small">
              You&apos;ll be the first admin. Additional users can be added
              from the Users page once you&apos;re in.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="auth-form-section-label">Your account</div>
            <label className="auth-field">
              <span className="auth-field-label">Your name</span>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                placeholder="Jane Smith"
              />
            </label>
            <label className="auth-field">
              <span className="auth-field-label">Work email</span>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                placeholder="jane@company.com"
                autoComplete="email"
              />
            </label>
            <label className="auth-field">
              <span className="auth-field-label">Password</span>
              <input
                type="password"
                value={form.password}
                onChange={(e) =>
                  setForm({ ...form, password: e.target.value })
                }
                required
                placeholder="Minimum 8 characters"
                autoComplete="new-password"
              />
            </label>

            <div
              className="auth-form-section-label"
              style={{ marginTop: 18 }}
            >
              Organization
            </div>
            <label className="auth-field">
              <span className="auth-field-label">Organization name</span>
              <input
                type="text"
                value={form.organization_name}
                onChange={(e) =>
                  setForm({ ...form, organization_name: e.target.value })
                }
                required
                placeholder="Acme Health"
              />
            </label>
            <label className="auth-field">
              <div className="auth-field-label-row">
                <span className="auth-field-label">URL slug</span>
                <span className="muted small">Used in URLs</span>
              </div>
              <input
                type="text"
                value={form.organization_slug}
                onChange={(e) =>
                  setForm({ ...form, organization_slug: e.target.value })
                }
                placeholder={effectiveSlug || "acme-health"}
              />
              {effectiveSlug && (
                <div className="auth-field-help">
                  Will be saved as <span className="mono">{effectiveSlug}</span>
                </div>
              )}
            </label>
            <label className="auth-field">
              <span className="auth-field-label">Description</span>
              <input
                type="text"
                value={form.organization_description}
                onChange={(e) =>
                  setForm({
                    ...form,
                    organization_description: e.target.value,
                  })
                }
                placeholder="Short description of what this org does"
              />
            </label>

            {error && (
              <div className="auth-error-banner">
                <span className="auth-error-dot" />
                {error}
              </div>
            )}

            <button
              type="submit"
              className="auth-submit"
              disabled={busy}
            >
              {busy ? (
                <>
                  <span className="auth-spinner" /> Creating…
                </>
              ) : (
                "Create organization"
              )}
            </button>
          </form>

          <div className="auth-form-footer">
            Already have an account? <Link to="/login">Sign in</Link>
          </div>
        </div>
      </main>
    </div>
  );
}
